/* import __COLOCATED_TEMPLATE__ from './customer-experience-attribute-filter.hbs'; */
/* RESPONSIBLE TEAM: team-reporting */
import Component from '@glimmer/component';
import { inject as service } from '@ember/service';
import { action } from '@ember/object';
import { tracked } from '@glimmer/tracking';
import { REPORTING_FILTER_SELECT_ALL } from 'embercom/lib/reporting/flexible/constants';
import type IntlService from 'ember-intl/services/intl';
import type { SelectedFilter } from 'embercom/services/filter-pattern-service';
import type ReportingUnderlyingDataService from 'embercom/services/reporting-underlying-data-service';
import { type AttributeMapping } from 'embercom/services/reporting-underlying-data-service';

interface Args {
  selected: SelectedFilter;
  loadKnownValuesSources: string[];
  analyticsObject: string;
  setSelected: (filterValues: string[], operator?: string) => void;
  removeFilter: () => void;
  initiallyOpened: boolean;
  icon: string;
  type: string;
  name: string;
  isDisabled: boolean;
}

export default class CustomerExperienceAttributeFilter extends Component<Args> {
  @service intercomEventService: $TSFixMe;
  @service intl!: IntlService;
  @service declare reportingUnderlyingDataService: ReportingUnderlyingDataService;

  @tracked label = this.currentLabel;
  // store the selection here so that the UI doesn't update until the drop down is closed
  @tracked selectedDropdownItems = this.args.selected.values || [];
  @tracked operator = this.args.selected.operator || 'category';
  @tracked availableValues: AttributeMapping = {};

  constructor(owner: unknown, args: Args) {
    super(owner, args);
    this.availableValues = this.reportingUnderlyingDataService.getAttributeKnownValues(
      'conversation_custom_fields#customer_experience_attributes',
    );
  }

  get selectedValues() {
    return this.selectedDropdownItems.filter(
      (item: string) => item !== REPORTING_FILTER_SELECT_ALL,
    );
  }

  get allItems() {
    return Object.entries(this.availableValues).map(([value, text]) => ({
      text: text as string,
      value,
      isSelected: this.selectedDropdownItems.includes(value),
    }));
  }

  get currentLabel() {
    if (
      this.selectedDropdownItems?.length &&
      this.selectedDropdownItems[0] !== REPORTING_FILTER_SELECT_ALL
    ) {
      return this.selectedDropdownItems
        .map((value: string) => this.availableValues[value])
        .join(', ');
    } else {
      return this.intl.t('components.reporting.custom.chart-builder.filter-bar.any');
    }
  }

  @action
  trackAnalytics() {
    this.intercomEventService.trackAnalyticsEvent({
      action: 'filtered_customer_experience_attribute',
      object: this.args.analyticsObject,
      attribute: this.selectedValues,
      operator: this.operator,
    });
  }

  @action
  onClose() {
    if (this.isDestroying) {
      // for some reason onClose gets triggered when you remove the filter
      // even if the dropdown isn't currently open
      return;
    }

    this.label = this.currentLabel;
  }
}

declare module '@glint/environment-ember-loose/registry' {
  export default interface Registry {
    'Reporting::Custom::ChartBuilder::FilterBar::CustomerExperienceAttributeFilter': typeof CustomerExperienceAttributeFilter;
  }
}
